import { FormProvider, useForm } from 'react-hook-form';
import { Flex, Text } from '@chakra-ui/react';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { ReviewFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/review-fields/review-form-fields';
import { generateValidationSchema } from 'src/utils/validation';
import {
  Banner,
  Button,
  ButtonText,
  OutlineCard,
  useToast
} from '@morressier/alembic';
import { useEarlyAssessmentFormDraft } from 'src/api/mutations';
import { useSubmitEarlyAssessmentReview } from 'src/api/commands';
import { Recommendation } from 'src/components/PeerReview/Recommendation';
import { Overlay } from 'src/components/Overlay';
import { useAssessmentData } from 'src/contexts/AssessmentDataProvider';
import { Layout } from 'src/components/Layout';
import { FormFieldRecommendedReviewers } from '@publishing-engine/domain/types/article';
import ExpertsRecommended from './ExpertsRecommended';
import { SubmissionInfoCard } from 'src/components/SubmissionInfo';
import { CustomFormFieldComponent } from 'src/components/FormField';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';
import { AssessmentFieldGroup } from 'src/components/Assessment/FieldGroup';
import { IExpertFirstAssessment } from '../Adjudications/adjudicationType';

interface Props {
  pageText: string;
  subHeading: string;
  onClose: () => void;
}

const AssessmentFeedbackForm: React.FC<Props> = ({
  onClose,
  pageText,
  subHeading
}) => {
  const {
    earlyAssessment,
    submissionId,
    earlyAssessmentFormSchema,
    submissionContext,
    workflowResponse,
    firstAssessmentFormDraft,
    invalidateAssessmentContextData,
    organizationId
  } = useAssessmentData();
  const ctxId = earlyAssessment.submissionContextId ?? '';
  const toast = useToast();

  const submitReviewMutation = useSubmitEarlyAssessmentReview({
    submissionId,
    reviewId: earlyAssessment.id,
    organizationId,
    submissionContextId: ctxId,
    articleWorkflowId: workflowResponse?.articleWorkflow?.id!,
    invalidateReviewContextData: invalidateAssessmentContextData
  });

  const schemaId =
    workflowResponse.articleWorkflow.formSchemas.reviewerFirstAssessment;
  const assessmentFormFields = new ReviewFormFields(
    earlyAssessmentFormSchema.documentSchema
  );
  const formDraftMutation = useEarlyAssessmentFormDraft();

  const configs = {
    Originality: assessmentFormFields.getFieldConfig('Originality'),
    Significance: assessmentFormFields.getFieldConfig('Significance'),
    Clarity: assessmentFormFields.getFieldConfig('Clarity'),
    Recommendation: assessmentFormFields.getFieldConfig('Recommendation'),

    FeedbackForAuthors:
      assessmentFormFields.getFieldConfig('FeedbackForAuthors'),
    EditorAttachments: assessmentFormFields.getFieldConfig('EditorAttachments'),
    AuthorAttachments: assessmentFormFields.getFieldConfig('AuthorAttachments'),
    RecommendedReviewers: assessmentFormFields.getFieldConfig(
      'RecommendedReviewers'
    ),
    FeedbackForEditors:
      assessmentFormFields.getFieldConfig('FeedbackForEditors'),
    PeerReviewTransparency: assessmentFormFields.getFieldConfig(
      'PeerReviewTransparency'
    ),
    ReviewerAnonymity: assessmentFormFields.getFieldConfig('ReviewerAnonymity'),
    WebOfScienceOptIn: assessmentFormFields.getFieldConfig('WebOfScienceOptIn')
  };

  const schema = generateValidationSchema(configs);

  const methods = useForm<IExpertFirstAssessment>({
    defaultValues: firstAssessmentFormDraft ?? {},
    resolver: ajvResolver(schema)
  });

  const { handleSubmit, getValues } = methods;

  const onSubmit = async () => {
    const formData = getValues();

    const response =
      pageText === 'Prepare Feedback'
        ? await formDraftMutation.mutateAsync({
          entityId: earlyAssessment.id,
          entityType: 'early_assessment',
          formType: 'externalEarlyAssessment',
          formSchemaId: schemaId!,
          data: formData
        }) : await submitReviewMutation.mutateAsync(formData);

    if (response.isSuccess) {
      toast({
        variant: 'success',
        title:
          pageText === 'Prepare Feedback'
            ? 'Feedback Prepared'
            : 'Feedback Submitted',
        description:
          pageText === 'Prepare Feedback'
            ? `You can review and edit your response again via “Prepare Feedback”.`
            : 'The Editors will be notified and will follow up as needed.',
        isClosable: true
      });
      onClose();
    } else {
      toast({
        variant: 'error',
        title: `${pageText} Unsuccessful`,
        description: `We encountered an issue while saving your ${pageText}. Please try again.`,
        isClosable: true
      });
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <Overlay height="100%">
          {/* ISSUE: When user open the overlay and they scroll to bottom they can see the page behind the overlay.
                        
                        NOTE: The height 100% in Overlay and below Flex component,
                        is temporary solution to cover available height for overlay. Doing that we can hide the page behind the overlay.
                    */}
          <Flex
            width="100vw"
            flexDirection="column"
            backgroundColor="surfaceSecondaryFill"
          >
            <form onSubmit={handleSubmit(onSubmit, scrollToFirstError)}>
              <Layout width="60rem">
                <Flex
                  flexDirection="column"
                  gap={8}
                  padding={16}
                  paddingBottom={8}
                >
                  <Flex flexDirection="column" gap={2}>
                    <Text textStyle="headingSection">{pageText}</Text>
                    <Text textStyle="textBody">{subHeading}</Text>
                  </Flex>
                  <OutlineCard borderless padding={8} width="100%">
                    <SubmissionInfoCard
                      headingText="SUBMISSION TO ASSESS"
                      showManuscript
                      entryPoint="early-assessment"
                    />
                  </OutlineCard>
                  {pageText === 'Prepare Feedback' && (
                    <Banner
                      variant="attention"
                      heading="For your attention"
                      content={`This response can also be edited during, and just before finalizing your decision. Your response will be sent to the Editors once you conclude.`}
                    />
                  )}

                  {configs.Originality &&
                    configs.Clarity &&
                    configs.Significance && (
                      <Flex
                        gap={12}
                        padding={6}
                        backgroundColor="surfacePrimaryFill"
                        flexDirection="column"
                      >
                        <AssessmentFieldGroup
                          fieldConfigs={[
                            configs.Originality,
                            configs.Clarity,
                            configs.Significance
                          ]}
                          title="Evaluation Criteria"
                        />
                      </Flex>
                    )}

                  <Flex
                    gap={12}
                    padding={6}
                    backgroundColor="surfacePrimaryFill"
                    flexDirection="column"
                  >
                    {configs.Recommendation && (
                      <Recommendation
                        config={configs.Recommendation}
                        textStyle={'headingGroup'}
                      />
                    )}
                  </Flex>
                  {configs.FeedbackForEditors && (
                    <Flex flexDirection="column">
                      <Text textStyle="headingGroup">
                        Communications For Editors
                      </Text>
                      <Flex
                        gap={4}
                        padding={6}
                        backgroundColor="surfacePrimaryFill"
                        flexDirection="column"
                      >
                        {configs.FeedbackForEditors && (
                          <CustomFormFieldComponent
                            config={configs.FeedbackForEditors}
                            placeholder="Add feedback to the editor"
                          />
                        )}
                        {configs.EditorAttachments && (
                          <CustomFormFieldComponent
                            config={configs.EditorAttachments}
                            submissionContextId={submissionContext.id}
                            submissionId={
                              earlyAssessment.caseData.submission.id
                            }
                          />
                        )}

                        {/* Recommended Reviewers */}
                        {configs.RecommendedReviewers && (
                          <ExpertsRecommended
                            config={
                              configs.RecommendedReviewers as FormFieldRecommendedReviewers
                            }
                          />
                        )}
                      </Flex>
                    </Flex>
                  )}
                  {configs.FeedbackForAuthors && (
                    <Flex flexDirection="column">
                      <Text textStyle="headingGroup">
                        Communications For Authors
                      </Text>
                      <Flex
                        gap={4}
                        padding={6}
                        backgroundColor="surfacePrimaryFill"
                        flexDirection="column"
                      >
                        {configs.FeedbackForAuthors && (
                          <CustomFormFieldComponent
                            config={configs.FeedbackForAuthors}
                            placeholder="Add feedback to the author"
                          />
                        )}
                        {configs.AuthorAttachments && (
                          <CustomFormFieldComponent
                            config={configs.AuthorAttachments}
                            submissionContextId={submissionContext.id}
                            submissionId={
                              earlyAssessment.caseData.submission.id
                            }
                          />
                        )}
                      </Flex>
                    </Flex>
                  )}

                  {/* Opt Ins */}
                  {(configs.PeerReviewTransparency &&
                    configs.ReviewerAnonymity) ||
                    configs.WebOfScienceOptIn && (
                      <Flex flexDirection="column">
                        <Text textStyle="headingGroup">Opt Ins</Text>
                        <Flex
                          gap={4}
                          paddingX={6}
                          paddingY={4}
                          backgroundColor="surfacePrimaryFill"
                          flexDirection="column"
                        >
                          <AssessmentFieldGroup
                            fieldConfigs={[
                              configs.PeerReviewTransparency,
                              configs.ReviewerAnonymity,
                              configs.WebOfScienceOptIn
                            ]}
                          />
                        </Flex>
                      </Flex>
                    )}
                </Flex>
              </Layout>
              <Flex
                padding={4}
                bottom={0}
                justifyContent="center"
                position="sticky"
                bgColor="surfacePrimaryFill"
              >
                <Flex width="100%" justifyContent="flex-end" gap={4}>
                  <ButtonText variant="caution" onClick={onClose}>
                    Cancel
                  </ButtonText>
                  {pageText === 'Submit Feedback' && (
                    <Button variant="primary" type="submit">
                      Submit Feedback
                    </Button>
                  )}
                  {pageText === 'Prepare Feedback' && (
                    <Button variant="primary" onClick={onSubmit}>
                      Prepare Feedback
                    </Button>
                  )}
                </Flex>
              </Flex>
            </form>
          </Flex>
        </Overlay>
      </FormProvider>
    </>
  );
};

export default AssessmentFeedbackForm;
